import {
  Box2d,
  Box2dModel,
  BoxLike,
  Editor,
  TLImageAsset,
  TLParentId,
  TLShape,
  TLShapeId,
  TLStoreSnapshot,
  Vec2d,
  VecLike,
} from '@tldraw/tldraw'
import { FrameShape } from '../Shapes/FrameShape/Types'
import { getOrderedFrames, repositionFrames } from '../Shapes/FrameShape/helpers'
import { FontRole, MediaTransformations } from 'types'
import { ImageShapeUtil } from '../Shapes/ImageShape/ImageShapeUtil'
import { isEqual } from 'lodash'
import { ImageShape } from '../Shapes/ImageShape/Types'
import { TextBoxShape, TextBoxShapeProps } from '../Shapes/TextBoxShape/Types'
import { CustomEqualitySet } from './utils/customEqualitySet'
import { Color as ColorType, GradientColor, SolidColor } from './Types'
import { GeoShapeUtil } from '../Shapes/GeoShape/GeoShapeUtil'
import { TextBoxShapeUtil } from '../Shapes/TextBoxShape/TextBoxShapeUtil'
import { GeoShape } from '../Shapes/GeoShape/Types'
import { FrameShapeUtil } from '../Shapes/FrameShape/FrameShapeUtil'
import { MaskShapeUtil } from '../Shapes/MaskShape/MaskShapeUtil'
import { MaskShape } from '../Shapes/MaskShape/Types'
import Color from 'colorjs.io'
import { pickNewColor } from './utils/colorise'
import { coloriseShape } from './utils/colorise'
import { getParamsForFileUpload } from 'utils'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { IMediaFile } from 'components/Design/UploadsTab/Types'
import { createOriginalAssetFromFile } from 'components/Design/UploadsTab/insertImage'
import { VideoShape } from '../Shapes/VideoShape/Types'
import { fitTextBox, getBiggestTextBox, getTextBoxes } from './utils/replaceFonts'
import { FontPairing } from 'constants/fontPairings'
import { VideoShapeUtil } from '../Shapes/VideoShape/VideoShapeUtil'

export const alignShapes = (
  editor: Editor,
  shapeIds: TLShapeId[],
  alignment: 'left' | 'center-horizontal' | 'right' | 'top' | 'center-vertical' | 'bottom',
) => {
  if (shapeIds.length === 1) {
    alignShapesToFrame(editor, shapeIds, alignment)
  } else {
    editor.alignShapes(shapeIds, alignment)
  }
}

export const alignShapesToFrame = (
  editor: Editor,
  shapeIds: TLShapeId[],
  alignment: 'left' | 'center-horizontal' | 'right' | 'top' | 'center-vertical' | 'bottom',
) => {
  const parentFrameId = editor.findCommonAncestor(shapeIds, (shape) => shape.type === 'frame')
  if (!parentFrameId) return

  editor.alignShapes([parentFrameId, ...shapeIds], alignment)
}

export const isOutsideFrame = (editor: Editor, shape: TLShape) => {
  const parentFrame = editor.findShapeAncestor(shape, (ancestor) => ancestor.type === 'frame') as FrameShape | undefined

  // Every shape should have a parent frame, except for frames
  if (!parentFrame) return

  const parentFrameBounds = editor.getShapePageBounds(parentFrame)
  const shapeBounds = editor.getShapePageBounds(shape)
  if (!shapeBounds || !parentFrameBounds) return

  return !shapeBounds.collides(parentFrameBounds)
}

export const deleteIfOutsideFrame = (editor: Editor, shape: TLShape) => {
  if (isOutsideFrame(editor, shape)) {
    editor.deleteShape(shape.id)
  }
}

export const getFramesInViewport = (editor: Editor): Array<{ frame: FrameShape; area: number }> => {
  const viewportBounds = editor.getViewportPageBounds()
  return getOrderedFrames(editor).map((frame) => {
    const frameBounds = editor.getShapeGeometry(frame).bounds
    const frameBox = new Box2d(frame.x, frame.y, frameBounds.width, frameBounds.height)
    const intersection = getIntersection(viewportBounds, frameBox)

    return { frame, area: intersection.w * intersection.h }
  })
}

export const getCurrentFrame = (editor: Editor, ignoreSelection = false) => {
  if (!ignoreSelection && editor.getSelectedShapes().length === 1 && editor.getSelectedShapes()[0].type === 'frame') {
    return editor.getSelectedShapes()[0] as FrameShape
  }
  const sortedByArea = getFramesInViewport(editor).sort((a, b) => b.area - a.area)
  if (sortedByArea.length === 0)
    return editor.getCurrentPageRenderingShapesSorted().find((shape) => shape.type === 'frame') as FrameShape
  const largestArea = sortedByArea[0].area
  const largestFrames = sortedByArea.filter((frame) => frame.area === largestArea)
  return largestFrames[largestFrames.length - 1].frame
}

export const getIntersection = (a: BoxLike, b: BoxLike): Box2dModel => {
  return {
    x: Math.max(a.x, b.x),
    y: Math.max(a.y, b.y),
    w: Math.max(Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x), 0),
    h: Math.max(Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y), 0),
  }
}

export const getColorisableShapes = (editor: Editor) =>
  editor.getCurrentPageRenderingShapesSorted().filter((shape) => {
    const shapeUtil = editor.getShapeUtil(shape)

    switch (shape.type) {
      case 'textBox':
        return (shapeUtil as TextBoxShapeUtil).isColorisable()
      case 'image':
        return (shapeUtil as ImageShapeUtil).isColorisable(shape as ImageShape)
      case 'frame':
        return (shapeUtil as FrameShapeUtil).isColorisable(shape as FrameShape)
      case 'mask':
        return (shapeUtil as MaskShapeUtil).isColorisable()
      case 'geometric':
        return (shapeUtil as GeoShapeUtil).isColorisable()
      case 'video':
        return false
      default:
        return true
    }
  })

export const getLightestOrDarkestColor = (palette: string[]) => {
  const colors = palette.map((color) => new Color(color))

  const colorsByLightness = colors
    .map((c) => ({ color: c, lightness: c.to('oklch').oklch.l }))
    .sort((a, b) => a.lightness - b.lightness)
    .map((entry) => entry.color)

  return Math.random() < 0.5 ? colorsByLightness.at(-1) : colorsByLightness.at(0)
}

export const applyPalette = (editor: Editor, palette: string[], startingColor?: Color) => {
  if (palette.length === 0) return
  const colors = palette.map((color) => new Color(color))

  const colorisableShapes = getColorisableShapes(editor)

  colorisableShapes.forEach((shape, i) => {
    const newColor = i === 0 && startingColor ? startingColor : pickNewColor(editor, shape, colors)
    coloriseShape(editor, shape, newColor)
  })
}

export const getMaskingShapeId = (editor: Editor): TLShapeId | null => {
  return (editor.getCurrentPage().meta.maskingShapeId as TLShapeId) || null
}

export const setMaskingShapeId = (editor: Editor, shapeId: TLShapeId | null) => {
  if (shapeId === getMaskingShapeId(editor)) return

  const currentPageId = editor.getCurrentPageId()

  if (shapeId === null) {
    editor.updatePage({ id: currentPageId, meta: { maskingShapeId: null } })
    return
  }

  const shape = editor.getShape(shapeId)

  if (!shape) return

  editor.updatePage({ id: currentPageId, meta: { maskingShapeId: shapeId } })
}

export const getSnapshotFontFamilies = (snapshot: TLStoreSnapshot) => {
  const fontFamilies = new Set<string>()

  Object.values(snapshot.store)
    .filter((thing): thing is TextBoxShape => 'type' in thing && thing.type === 'textBox')
    .map((shape) => fontFamilies.add(shape.props.fontFamily))

  return Array.from(fontFamilies)
}

export const getColors = (editor: Editor) => {
  const colors = new CustomEqualitySet<SolidColor | GradientColor>(isEqual)

  editor.getCurrentPageShapes().forEach((shape) => {
    if (shape.type === 'frame') {
      const util = editor.getShapeUtil(shape.type) as FrameShapeUtil
      util.getColors(shape as FrameShape).forEach((color) => colors.add(color))
    }

    if (shape.type === 'geometric') {
      const util = editor.getShapeUtil(shape.type) as GeoShapeUtil
      util.getColors(shape as GeoShape).forEach((color) => colors.add(color))
    }

    if (shape.type === 'textBox') {
      const util = editor.getShapeUtil(shape.type) as TextBoxShapeUtil
      util.getColors(shape as TextBoxShape).forEach((color) => colors.add(color))
    }

    if (shape.type === 'image') {
      const util = editor.getShapeUtil(shape.type) as ImageShapeUtil
      util.getColors(shape as ImageShape).forEach((color) => colors.add(color))
    }

    if (shape.type === 'mask') {
      const util = editor.getShapeUtil(shape.type) as MaskShapeUtil
      util.getColors(shape as MaskShape).forEach((color) => colors.add(color))
    }
  })

  return colors
}

export const isFullyTransparent = (color: ColorType) => {
  return color.type === 'linearGradient' ? color.stops.every((s) => s.opacity === 0) : color.opacity === 0
}

export const hasTransparency = (color: ColorType) => {
  return color.type === 'linearGradient' ? color.stops.some((s) => s.opacity < 1) : color.opacity < 1
}

/**
 * Returns the shapes colliding with the given shape.
 */
export const getCollidingShapes = (editor: Editor, shape: TLShape): TLShape[] => {
  const shapes = editor.getCurrentPageRenderingShapesSorted()

  const shapeBox = editor.getShapePageBounds(shape)

  if (!shapeBox) return []

  return shapes.filter((s) => {
    const sBox = editor.getShapePageBounds(s)
    if (!sBox) return false
    return s.id !== shape.id && shapeBox.collides(sBox)
  })
}

/**
 * Returns the shapes behind the given shape.
 * A shape is considered behind another shape if its index is lower and it collides with the given shape.
 * Text boxes are skipped when they are behind other text boxes because their
 * overlap is not visually significant.
 */
export const getShapesBehind = (editor: Editor, shape: TLShape) => {
  const shapes = getCollidingShapes(editor, shape)

  const shapeIndex = editor.getCurrentPageRenderingShapesSorted().findIndex((s) => s.id === shape.id)

  return shapes.filter((s, i) => {
    if (s.type === 'textBox' && shape.type === 'textBox') {
      return false
    }
    if (getShapeColor(editor, s).alpha < 0.2) {
      return false
    }
    return i < shapeIndex
  })
}

/**
 * Returns the fill color of the shape.
 */
export const getShapeColor = (editor: Editor, shape: TLShape) => {
  if (shape.type === 'frame') {
    const frame = shape as FrameShape
    return frame.props.fill.type === 'solidColor'
      ? new Color(frame.props.fill.color)
      : new Color(frame.props.fill.stops[0].color)
  }

  if (shape.type === 'geometric') {
    const geo = shape as GeoShape
    const color = geo.props.fill.type === 'solidColor' ? geo.props.fill : geo.props.fill.stops[0]

    return new Color({
      alpha: color.opacity,
      coords: new Color(color.color).coords,
      space: 'sRGB',
    })
  }

  if (shape.type === 'textBox') {
    return new Color((shape as TextBoxShape).props.color)
  }

  if (shape.type === 'image' && shape.meta?.colorSet) {
    const util = editor.getShapeUtil(shape.type) as ImageShapeUtil
    const colors = Array.from(util.getColors(shape as ImageShape))
    if (colors.length > 0) return new Color(colors[0].color)
  }

  return new Color('#000000')
}

/**
 * Returns the color of the shape behind the given shape. If there is no shape behind, returns black.
 * The shape behind is the shape with the most overlap with the given shape.
 */
export const getBackdropColor = (editor: Editor, shape: TLShape): Color => {
  const behind = getBackdropShape(editor, shape)
  if (!behind) return new Color('#000000')

  return getShapeColor(editor, behind)
}

/**
 * Return shapes between a and b, back to front. A must be in front of B.
 */
export const getShapesBetween = (editor: Editor, a: TLShape, b: TLShape) => {
  const shapes = editor.getCurrentPageRenderingShapesSorted()

  const aIndex = shapes.findIndex((shape) => shape.id === a.id)
  const bIndex = shapes.findIndex((shape) => shape.id === b.id)

  return shapes.slice(bIndex + 1, aIndex)
}

/**
 * Returns the area of the visual overlap between two shapes. A must be in front of B.
 */
export const getOverlap = (editor: Editor, a: TLShape, b: TLShape): number => {
  const aBounds = editor.getShapePageBounds(a)
  const bBounds = editor.getShapePageBounds(b)

  if (!aBounds || !bBounds) return 0

  const intersectionAB = getIntersection(aBounds, bBounds)

  let pairs: Array<[TLShape, BoxLike]> = getShapesBetween(editor, a, b)
    .reverse()
    .map((shape) => {
      const aBounds = editor.getShapePageBounds(a)
      const shapeBounds = editor.getShapePageBounds(shape)

      if (!aBounds || !shapeBounds) return [shape, Box2d.From({ x: 0, y: 0, w: 0, h: 0 })]

      let intersection = getIntersection(aBounds, shapeBounds)

      return [shape, intersection]
    })

  if (pairs.length === 0) {
    return intersectionAB.w * intersectionAB.h
  }

  return Math.max(
    pairs.reduce((acc, [_, intersection], i) => {
      if (i === 0) {
        const int = getIntersection(intersectionAB, intersection)
        return acc - int.w * int.h
      } else {
        const previousIntersection = pairs[i - 1][1]
        const intersectionWithPreviousIntersection = getIntersection(previousIntersection, intersection)
        return (
          acc -
          intersection.w * intersection.h +
          intersectionWithPreviousIntersection.w * intersectionWithPreviousIntersection.h
        )
      }
    }, intersectionAB.w * intersectionAB.h),
    0,
  )
}

/**
 * Returns the shape behind the given shape with the most overlap.
 */
export const getBackdropShape = (editor: Editor, shape: TLShape) => {
  const shapes = getShapesBehind(editor, shape).reverse()

  if (shapes.length === 0) return undefined

  return shapes.reduce((acc, s) => {
    if (getOverlap(editor, shape, s) > getOverlap(editor, shape, acc)) {
      return s
    }
    return acc
  }, shapes[0])
}

/**
 * Returns the background image for a given parent (a frame).
 * If no background image is found, returns undefined.
 *
 * @param editor The editor instance.
 * @param parentId The parent id.
 * @returns ImageShape | undefined
 */
export const getBackgroundImage = (editor: Editor, parentId: TLParentId): ImageShape | undefined => {
  return editor
    .getSortedChildIdsForParent(parentId)
    .map((id) => editor.getShape(id))
    .find((shape): shape is ImageShape => shape?.type === 'image' && (shape as ImageShape).props.isBackground)
}

/**
 * Returns the video for a given parent (a frame).
 * If no video is found, returns undefined.
 *
 * @param editor The editor instance.
 * @param parentId The parent id.
 * @returns VideoShape | undefined
 */
export const getVideo = (editor: Editor, parentId: TLParentId): VideoShape | undefined => {
  return editor
    .getSortedChildIdsForParent(parentId)
    .map((id) => editor.getShape(id))
    .find((shape): shape is VideoShape => shape?.type === 'video')
}

/**
 * Finds the asset with the given media file id and transformations.
 *
 * @param editor The editor instance.
 * @param mediaFileId The media file id.
 * @param transformations The transformations.
 */
export const findAssetByTransformations = (
  editor: Editor,
  mediaFileId: string,
  transformations: MediaTransformations,
) => {
  return editor.getAssets().find((asset): asset is TLImageAsset => {
    if (!asset.meta.mediaFileId || !asset.meta.transformations) return false

    return asset.meta.mediaFileId === mediaFileId && isEqual(asset.meta.transformations, transformations)
  })
}

export const pageToViewport = (editor: Editor, point: VecLike) => {
  const { x: cx, y: cy, z: cz = 1 } = editor.getCamera()
  return new Vec2d((point.x + cx) * cz, (point.y + cy) * cz, point.z ?? 0.5)
}

export const getImageShapes = (editor: Editor) => {
  return editor
    .getCurrentPageShapes()
    .filter((shape): shape is ImageShape | MaskShape => shape.type === 'mask' || shape.type === 'image')
}

export const getReplaceableImageShapes = (editor: Editor, includeReplaced = false) => {
  return getImageShapes(editor).filter((shape) => {
    if (shape.type === 'image') {
      if (includeReplaced) {
        return shape.props.isReplaceable || shape.props.wasReplaced
      }
      const util = editor.getShapeUtil(shape) as ImageShapeUtil
      return util.canBeReplaced(shape as ImageShape)
    }
    if (shape.type === 'mask') {
      if (includeReplaced) {
        return shape.props.isReplaceable || shape.props.wasReplaced
      }
      const util = editor.getShapeUtil(shape) as MaskShapeUtil
      return util.canBeReplaced(shape as MaskShape)
    }
    return false
  })
}

export const getVideoShapes = (editor: Editor) => {
  return editor.getCurrentPageShapes().filter((shape): shape is VideoShape => shape.type === 'video')
}

export const getReplaceableVideoShapes = (editor: Editor) => {
  return getVideoShapes(editor).filter((shape) => {
    const util = editor.getShapeUtil(shape) as VideoShapeUtil
    return util.canBeReplaced(shape)
  })
}

export const handleUpload = async (file: File) => {
  const paramsRes = await getParamsForFileUpload(file)

  if (paramsRes) {
    const { url, method, body, error } = paramsRes

    if (!error && url) {
      const res = await fetch(url, {
        method,
        body,
      })

      if (res.ok) {
        return res.url.split('?')[0]
      }
    }
  }

  showToastMessage('File upload failed', 'error')
}

export const getPlaceholderLogo = (editor: Editor) => {
  const shapes = editor.getCurrentPageRenderingShapesSorted()
  return shapes.find((shape): shape is ImageShape => shape.type === 'image' && (shape as ImageShape).props.isLogo)
}

export const replacePlaceholderLogo = async (editor: Editor, logo: IMediaFile) => {
  const placeholderLogo = getPlaceholderLogo(editor)
  if (placeholderLogo && logo) {
    const util = editor.getShapeUtil(placeholderLogo) as ImageShapeUtil
    const asset = await createOriginalAssetFromFile(logo)
    editor.createAssets([asset])
    util.replace(placeholderLogo, asset.id, { isLogo: true })
  }
}

export const getIsEditable = (editor: Editor) => {
  const selectedShapes = editor.getSelectedShapes()

  if (!selectedShapes) {
    return false
  }

  return selectedShapes.every(
    (shape) =>
      shape.type === 'group' ||
      (shape as ImageShape | MaskShape | GeoShape | TextBoxShape | FrameShape | VideoShape).props?.isEditable,
  )
}

export const getIsPreview = (editor: Editor): boolean => {
  return editor.getCurrentPage().meta.isPreview as boolean
}

export const setIsPreview = (editor: Editor, isPreview: boolean) => {
  const currentPageId = editor.getCurrentPageId()
  editor.updatePage({ id: currentPageId, meta: { isPreview } })
}

export const getAutoplayVideo = (editor: Editor): boolean => {
  const autoplayVideo = editor.getCurrentPage().meta.autoplayVideo

  return autoplayVideo !== undefined ? (autoplayVideo as boolean) : true
}

export const setAutoplayVideo = (editor: Editor, autoplayVideo: boolean) => {
  const currentPageId = editor.getCurrentPageId()
  editor.updatePage({ id: currentPageId, meta: { autoplayVideo } })
}

/**
 * Extract title and body text styles from the current editor.
 * If there are no text boxes, returns default font pairings.
 * If there are text boxes, it returns the style of the biggest text box as the
 * title style and the style of the first smaller text box as the body style.
 * If there is only one text box, it returns the style of that text box as the
 * title style and body style.
 * @param editor The editor instance.
 * @returns FontPairing
 */
export const getTextStyles = (editor: Editor): FontPairing | [] => {
  const textBoxes = getTextBoxes(editor)
  if (textBoxes.length === 0) return []

  const biggestTextBox = getBiggestTextBox(textBoxes)
  const titleStyle = {
    font_family: biggestTextBox.props.fontFamily,
    font_weight: biggestTextBox.props.fontWeight,
    italic: biggestTextBox.props.italic,
  }

  const smallerTextBox = textBoxes.find((shape) => shape.id !== biggestTextBox.id) || biggestTextBox

  const bodyStyle = {
    font_family: smallerTextBox.props.fontFamily,
    font_weight: smallerTextBox.props.fontWeight,
    italic: smallerTextBox.props.italic,
  }

  return [
    { font_role: FontRole.Title, style: titleStyle },
    { font_role: FontRole.Body, style: bodyStyle },
  ]
}

/**
 * Resize and reposition shapes to fit within the specified width and height.
 * Shapes are scaled uniformly based on a scale factor determined by the change in frame dimensions.
 *
 * Shapes are repositioned relative to the center of the original and resized frame,
 * maintaining their spatial relationship to the frame center.
 *
 * Every frame on the page (i.e. every slide of a carousel) is resized to the same target
 * dimensions, each frame's own content is scaled within it, and the frames are then re-stacked
 * so the resized slides don't overlap.
 *
 * @param editor The editor instance.
 * @param dimensions The target width and height for each frame.
 * @param fileSupportsCaption Whether the file's channel supports a caption, which adds extra
 *   spacing after the first slide when re-stacking.
 */
export const resize = (
  editor: Editor,
  dimensions: { width: number; height: number },
  { fileSupportsCaption = false }: { fileSupportsCaption?: boolean } = {},
) => {
  const { width, height } = dimensions
  const frames = getOrderedFrames(editor)

  if (frames.length === 0) {
    return
  }

  const frameIds = new Set<TLParentId>(frames.map((aFrame) => aFrame.id))
  // For a single frame, keep the original behaviour exactly: every non-frame, non-group shape
  // belongs to that frame regardless of where it sits. With multiple frames, content is assigned
  // to a frame by parentage (its own children) or, for page/group-level shapes, by bounds.
  const isSingleFrame = frames.length === 1
  const currentFrameId = getCurrentFrame(editor).id
  const shapes = editor.getCurrentPageShapes()

  editor.batch(() => {
    frames.forEach((frame) => {
      const existingFrameBounds = editor.getShapePageBounds(frame)

      editor.updateShape({
        id: frame.id,
        type: 'frame',
        props: { w: width, h: height },
      })

      const newFrameBounds = editor.getShapePageBounds(frame)

      if (!existingFrameBounds || !newFrameBounds) {
        return
      }

      const existingFrameCenterX = existingFrameBounds.x + existingFrameBounds.w / 2
      const existingFrameCenterY = existingFrameBounds.y + existingFrameBounds.h / 2
      const newFrameCenterX = newFrameBounds.x + newFrameBounds.w / 2
      const newFrameCenterY = newFrameBounds.y + newFrameBounds.h / 2

      const scale = Math.min(width / existingFrameBounds.width, height / existingFrameBounds.height)

      // scale and reposition the shapes belonging to this frame
      shapes
        .filter((shape) => {
          if (shape.id === frame.id || shape.type === 'group' || shape.type === 'frame') {
            return false
          }
          if (shape.parentId === frame.id) {
            return true
          }
          // a child of a different frame belongs to that frame, not this one
          if (frameIds.has(shape.parentId)) {
            return false
          }
          if (isSingleFrame) {
            return true
          }
          const shapeBounds = editor.getShapePageBounds(shape)
          if (!shapeBounds) {
            return false
          }
          const centerX = shapeBounds.x + shapeBounds.w / 2
          const centerY = shapeBounds.y + shapeBounds.h / 2
          return (
            centerX >= existingFrameBounds.x &&
            centerX <= existingFrameBounds.x + existingFrameBounds.w &&
            centerY >= existingFrameBounds.y &&
            centerY <= existingFrameBounds.y + existingFrameBounds.h
          )
        })
        .forEach((shape) => {
          const shapeBounds = editor.getShapePageBounds(shape)

          if (!shapeBounds) {
            return
          }

          const existingCenterX = shapeBounds.x + shapeBounds.w / 2
          const existingCenterY = shapeBounds.y + shapeBounds.h / 2

          const offsetX = existingCenterX - existingFrameCenterX
          const offsetY = existingCenterY - existingFrameCenterY

          const newCenterX = newFrameCenterX + offsetX * scale
          const newCenterY = newFrameCenterY + offsetY * scale

          const newW = shapeBounds.w * scale
          const newH = shapeBounds.h * scale

          // The math above is in page coordinates. A frame's children store their position
          // relative to the frame, so convert back to local space before writing.
          const isFrameChild = shape.parentId === frame.id
          const localOriginX = isFrameChild ? newFrameBounds.x : 0
          const localOriginY = isFrameChild ? newFrameBounds.y : 0

          const newX = newCenterX - newW / 2 - localOriginX
          const newY = newCenterY - newH / 2 - localOriginY

          if (shape.type === 'textBox') {
            const shapeUtil = editor.getShapeUtil(shape) as TextBoxShapeUtil
            const existingDimensions = shapeUtil.getDimensions(shape.props as TextBoxShapeProps)
            const newDimensions = { width: existingDimensions.width * scale, height: existingDimensions.height * scale }

            if (scale < 1) {
              editor.updateShape(
                fitTextBox(
                  editor,
                  {
                    ...shape,
                    x: newX,
                    y: newY,
                    props: {
                      ...shape.props,
                      w: newDimensions.width,
                      autoSize: false,
                    },
                  } as TextBoxShape,
                  newDimensions,
                ),
              )

              return
            }

            editor.updateShape({
              ...shape,
              x: newX,
              y: newY,
              props: {
                ...shape.props,
                w: newDimensions.width,
                fontSize: (shape as TextBoxShape).props.fontSize * scale,
              },
            })

            return
          }

          editor.updateShape({
            id: shape.id,
            type: shape.type,
            x: newX,
            y: newY,
            props: {
              ...shape.props,
              w: newW,
              h: newH,
            },
          })
        })
    })

    // Re-stack the resized frames so slides don't overlap.
    editor.updateShapes(repositionFrames(getOrderedFrames(editor), fileSupportsCaption))

    const resizedCurrentFrameBounds = editor.getShapePageBounds(currentFrameId)

    if (resizedCurrentFrameBounds) {
      // reposition the camera. in case user is on mobile, briefly enable camera movement.
      const canCurrentlyMoveCamera = editor.getInstanceState().canMoveCamera
      editor.updateInstanceState({ canMoveCamera: true })
      editor.zoomToBounds(resizedCurrentFrameBounds, undefined, { duration: 500, easing: (t) => t * (2 - t) })
      editor.updateInstanceState({ canMoveCamera: canCurrentlyMoveCamera })
    }
    editor.history.clear()
  })
}
